import { useState } from "react";
import type { FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_URL = "https://readdy.ai/api/form/d9vv7bg3256a3o8kooag";

const testimonials = [
  { name: "小满", role: "追漫读者", text: "《满溢游泳池》真的太治愈了，画面又美，每周更新从不缺席，免费就能看完整章节，太良心了。" },
  { name: "阿澈", role: "忠实读者", text: "分类很清晰，找恋爱、校园的漫画特别方便，评论区氛围也好，是我最常来的漫画站。" },
  { name: "晚风", role: "漫画爱好者", text: "高清画面加载快，手机上看体验也很好，更新的新作质量都很高，值得推荐。" },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们-178漫画网",
    url: "https://swimchats.github.io/about",
    description: "178漫画网提供免费漫画在线阅读服务，专注恋爱、校园、日常、治愈等题材，无需付费即可阅读完整章节与高清画面。",
    isPartOf: { "@type": "WebSite", name: "178漫画网", url: "https://swimchats.github.io/" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://swimchats.github.io/" },
      { "@type": "ListItem", position: 2, name: "关于我们" },
    ],
  },
];

export default function AboutPage() {
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("phone_alt") as string) || "";

    setFormError("");
    setFormSuccess(false);

    if (honeypot.trim() !== "") {
      setFormSuccess(true);
      form.reset();
      return;
    }

    const payload = new FormData(form);
    payload.delete("phone_alt");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        responseText.toLowerCase().includes("spam") ||
        serverMsg.toLowerCase().includes("spam");

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="关于我们-品牌介绍与联系方式-178漫画网"
        description="了解178漫画网：一家提供免费漫画在线阅读的内容平台，专注恋爱、校园、日常、治愈等题材，无需付费即可阅读完整章节与高清画面。联系我们请通过页面表单或客服邮箱。"
        keywords="178漫画网,关于我们,免费漫画,联系客服"
        canonical="https://swimchats.github.io/about"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-28 pb-14">
        <div className="pt-4">
          <Breadcrumb items={[{ label: "关于我们" }]} />
        </div>

        <header className="mt-6 rounded-lg border border-background-200 bg-background-100 px-6 py-10 text-center md:py-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-sm font-medium text-accent-700">
            <i className="ri-flag-line"></i>关于我们
          </span>
          <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">让每一部好漫画，都能被免费读到</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-foreground-600">
            178漫画网是一家中文漫画在线阅读平台，致力于为用户提供免费、高清、流畅的漫画阅读体验。从《满溢游泳池》这样的清新恋爱作品，到校园、日常、治愈、搞笑等丰富题材，我们努力让每一位读者都能在这里找到属于自己的故事。
          </p>
        </header>

        <section className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { num: "1000+", label: "在线漫画", icon: "ri-book-open-line" },
            { num: "500万+", label: "累计阅读", icon: "ri-eye-line" },
            { num: "每周更新", label: "稳定追更", icon: "ri-refresh-line" },
            { num: "100%", label: "免费阅读", icon: "ri-hand-heart-line" },
          ].map((s, i) => (
            <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className={`${s.icon} text-xl`}></i>
              </span>
              <p className="mt-3 font-heading text-xl text-foreground-950">{s.num}</p>
              <p className="mt-1 text-xs text-foreground-500">{s.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-12">
          <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">我们的理念</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              { title: "免费初心", desc: "好内容不该被付费门槛阻挡。我们坚持免费在线阅读，让更多人轻松享受漫画的乐趣。", icon: "ri-gift-line" },
              { title: "高清体验", desc: "我们持续优化加载与渲染，为读者提供高清、流畅、舒适的阅读画面与竖屏适配。", icon: "ri-hd-line" },
              { title: "稳定更新", desc: "热门漫画每周稳定更新，配合分类、榜单与推荐，让你轻松追更不迷路。", icon: "ri-calendar-check-line" },
            ].map((v, i) => (
              <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 border-y border-background-200 bg-background-100 py-12">
          <div className="text-center">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">读者口碑</h2>
            <p className="mt-2 text-sm text-foreground-600">来自忠实读者们的真实评价</p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <figure key={i} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex gap-0.5 text-accent-500">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-6 text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-heading text-primary-700">{t.name.slice(0, 1)}</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-2 text-sm text-foreground-600">任何建议、问题或版权合作，欢迎随时联系</p>
              <ul className="mt-6 flex flex-col gap-4 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className="ri-mail-line"></i></span>
                  客服邮箱：service@178manga.com
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700"><i className="ri-customer-service-2-line"></i></span>
                  在线客服：每日 09:00 - 21:00
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-900"><i className="ri-wechat-line"></i></span>
                  微信公众号：178漫画网
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-900"><i className="ri-weibo-line"></i></span>
                  官方微博：@178漫画网
                </li>
              </ul>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 text-xs leading-6 text-foreground-600">
                <p className="font-semibold text-foreground-800">版权与合作</p>
                <p className="mt-1">本站内容仅供学习交流使用。如涉及版权问题，请通过客服邮箱与我们联系，我们将在核实后尽快处理。</p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950">给我们留言</h3>
              <p className="mt-1 text-xs text-foreground-500">我们会认真阅读每一份反馈</p>
              <form data-readdy-form="d9vv7bg3256a3o8kooag" onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
                <div className="flex flex-col gap-4 sm:flex-row">
                  <div className="flex-1">
                    <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-700">您的称呼</label>
                    <input id="name" name="name" type="text" placeholder="请输入称呼" className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">联系邮箱</label>
                    <input id="email" name="email" type="email" placeholder="name@example.com" className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm text-foreground-700">留言内容</label>
                  <textarea id="message" name="message" rows={5} maxLength={500} placeholder="请输入你想说的话，最多500字" className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">500 / 500</p>
                </div>
                <div className="hp-field" aria-hidden="true">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
                </div>
                {formError && (
                  <p className="rounded-md bg-primary-100 px-3 py-2 text-sm text-primary-800">{formError}</p>
                )}
                {formSuccess && (
                  <p className="rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-800">留言已发送，感谢你的反馈！</p>
                )}
                <button type="submit" className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-send-plane-line"></i>提交留言
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}